/**
 * <s-live-schedule> — Turns the TikTok "live" state on and off from a weekly schedule.
 *
 * Adds or removes `live-tcg` / `live-store` classes on <html>; every live-aware element
 * (top bar, homepage strip, Live Auctions cards, menu dot) shows or hides from those
 * classes with CSS. A channel whose manual "is live now" switch is on keeps its class
 * (rendered by Liquid) and is never removed here.
 *
 * Attributes:
 *   data-timezone       — IANA time zone the schedule is written in (default Europe/London).
 *   data-schedule-tcg   — Schedule text for the TCG channel, one slot per line.
 *   data-schedule-store — Schedule text for the main channel, one slot per line.
 *   data-manual-tcg / data-manual-store — Present when the manual switch is on.
 *
 * Schedule lines look like: "Mon 19:00-21:00", "Mon-Fri 18:30-20:00", "Sat, Sun 12:00-14:00",
 * "Daily 20:00-22:00", "Weekdays 19:00-21:00", "Weekends 11:00-13:00". 24-hour times.
 * A slot whose end is earlier than its start runs past midnight.
 */
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** @param {string} token */
function dayIndex(token) {
  return DAYS.indexOf(token.slice(0, 3));
}

/** @param {string} text @returns {Set<number>} */
function parseDays(text) {
  const days = new Set();
  const lower = text.toLowerCase();
  if (/daily|every ?day/.test(lower)) DAYS.forEach((_, i) => days.add(i));
  if (/weekday/.test(lower)) [1, 2, 3, 4, 5].forEach((i) => days.add(i));
  if (/weekend/.test(lower)) [0, 6].forEach((i) => days.add(i));
  for (const part of lower.split(/[,&/]|\band\b/)) {
    const range = part.match(/([a-z]{3})[a-z]*\s*(?:-|–|to)\s*([a-z]{3})[a-z]*/);
    if (range) {
      let start = dayIndex(range[1]);
      const end = dayIndex(range[2]);
      if (start < 0 || end < 0) continue;
      for (let guard = 0; guard < 7; guard++) {
        days.add(start);
        if (start === end) break;
        start = (start + 1) % 7;
      }
      continue;
    }
    for (const word of part.match(/[a-z]+/g) || []) {
      const index = dayIndex(word);
      if (index >= 0 && word.length >= 3) days.add(index);
    }
  }
  return days;
}

/** @param {string} schedule @returns {Array<{days: Set<number>, start: number, end: number}>} */
function parseSchedule(schedule) {
  const slots = [];
  for (const line of schedule.split(/\n|;/)) {
    const time = line.match(/(\d{1,2})[:.](\d{2})\s*(?:-|–|to)\s*(\d{1,2})[:.](\d{2})/);
    if (!time) continue;
    const days = parseDays(line.slice(0, time.index));
    if (days.size === 0) continue;
    slots.push({
      days,
      start: Number(time[1]) * 60 + Number(time[2]),
      end: Number(time[3]) * 60 + Number(time[4]),
    });
  }
  return slots;
}

/** @param {string} timeZone @returns {{day: number, minutes: number}} */
function nowIn(timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return { day: dayIndex(get('weekday').toLowerCase()), minutes: Number(get('hour')) * 60 + Number(get('minute')) };
}

/** @param {ReturnType<typeof parseSchedule>} slots @param {{day: number, minutes: number}} now */
function isLive(slots, now) {
  const yesterday = (now.day + 6) % 7;
  return slots.some((slot) => {
    if (slot.end > slot.start) {
      return slot.days.has(now.day) && now.minutes >= slot.start && now.minutes < slot.end;
    }
    return (slot.days.has(now.day) && now.minutes >= slot.start) || (slot.days.has(yesterday) && now.minutes < slot.end);
  });
}

class LiveSchedule extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), 30000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #update() {
    const timeZone = this.dataset.timezone || 'Europe/London';
    let now;
    try {
      now = nowIn(timeZone);
    } catch {
      return;
    }
    const root = document.documentElement;
    for (const channel of ['tcg', 'store']) {
      const manual = this.hasAttribute(`data-manual-${channel}`);
      if (manual) continue;
      const slots = parseSchedule(this.getAttribute(`data-schedule-${channel}`) || '');
      root.classList.toggle(`live-${channel}`, isLive(slots, now));
    }
  }
}

if (!customElements.get('s-live-schedule')) customElements.define('s-live-schedule', LiveSchedule);
